// coach/services/coachAPI.js
import { API_CONFIG, buildApiUrl } from '../firebase/config';
import { auth } from '../firebase/config';

// Helper function to get coachId from localStorage
const getCoachId = () => {
  try {
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    if (!currentUser || (!currentUser.uid && !currentUser.id)) {
      throw new Error('No coach ID found in localStorage. Please log in again.');
    }
    return currentUser.id || currentUser.uid;
  } catch (error) {
    console.error('Error getting coach ID from localStorage:', error);
    throw new Error('Authentication required. Please log in again.');
  }
};

// Helper function to get Firebase ID token
const getIdToken = async () => {
  try {
    if (!auth.currentUser) {
      throw new Error('No authenticated user found');
    }
    const idToken = await auth.currentUser.getIdToken(true);
    return idToken;
  } catch (error) {
    console.error('Error getting ID token:', error);
    throw new Error('Authentication required. Please log in again.');
  }
};

// Helper function for API calls
const coachApiCall = async (url, options = {}) => {
  try {
    // Get Firebase ID token and add to Authorization header
    const idToken = await getIdToken();
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
        ...options.headers
      },
      ...options
    });
    
    if (!response.ok) {
      let errorData;
      let errorText = '';
      
      try {
        // First get the raw response text for debugging
        errorText = await response.text();
        console.log('Raw error response:', errorText);
        
        // Try to parse as JSON
        errorData = JSON.parse(errorText);
      } catch (parseError) {
        console.log('Failed to parse error response as JSON:', parseError);
        // If JSON parsing fails, create a simple error object
        errorData = { error: response.statusText };
      }
      
      // Safely access error message with fallbacks
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      if (errorData && typeof errorData === 'object') {
        if (typeof errorData.error === 'string' && !errorData.error.includes('rows')) {
          errorMessage = errorData.error;
        } else if (typeof errorData.message === 'string' && !errorData.message.includes('rows')) {
          errorMessage = errorData.message;
        }
      }
      
      // If the error message contains problematic code, use a generic message
      if (errorMessage.includes('rows') || errorMessage.includes('Cannot access')) {
        errorMessage = `Server error (${response.status}): Unable to process request`;
      }
      
      console.log('Final error message:', errorMessage);
      throw new Error(errorMessage);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Coach API call failed:', error);
    throw error;
  }
};

/** Public coach self-registration — uses dedicated API route so approval is always pending until admin acts. */
export const registerCoachPortal = async (coachPayload) => {
  const url = buildApiUrl(API_CONFIG.endpoints.coaches.portalRegister);
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...coachPayload,
      registrationSource: 'coach_portal',
      role: 'coach'
    })
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || `Registration failed: ${response.statusText}`);
  }
  const json = await response.json();
  return json.data ?? json;
};

/** Accept coach invitation (sets password, activates account). */
export const acceptCoachInvitation = async ({ token, email, password }) => {
  const url = buildApiUrl('/coaches/accept-invitation');
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, email, password })
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Invitation acceptance failed');
  }
  const json = await response.json();
  return json.data ?? json;
};



/** Get onboarding progress for a coach. */
export const getOnboardingProgress = async (coachId) => {
  const url = buildApiUrl(`/coaches/${coachId}/onboarding`);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch onboarding progress');
  }
  const json = await response.json();
  return json.data ?? json;
};

/** Mark onboarding as complete. */
export const completeOnboarding = async (coachId) => {
  const url = buildApiUrl(`/coaches/${coachId}/onboarding/complete`);
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to complete onboarding');
  }
  const json = await response.json();
  return json.data ?? json;
};

/** Sign a coaching agreement document. */
export const signAgreement = async (coachId, agreementType, signatureData) => {
  const url = buildApiUrl(`/coaches/${coachId}/agreements`);
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ agreementType, signatureData })
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to sign agreement');
  }
  const json = await response.json();
  return json.data ?? json;
};

/** Fetch training courses for onboarding. */
export const getTrainingCourses = async () => {
  const url = buildApiUrl('/courses');
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch training courses');
  }
  const json = await response.json();
  return json.data ?? json;
};

/** Fetch curriculum items for a specific course. */
export const getCurriculumByCourse = async (courseId) => {
  const url = buildApiUrl(`/curriculum/course/${courseId}`);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to fetch curriculum items');
  }
  const json = await response.json();
  return json.data ?? json;
};

/** Mark training course as completed for a coach. */
export const completeTrainingCourse = async (coachId, courseId) => {
  const url = buildApiUrl(`/coaches/${coachId}/training/${courseId}/complete`);
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.message || 'Failed to complete training course');
  }
  const json = await response.json();
  return json.data ?? json;
};

/** Locations list for signup form (same source as admin coach form). */
export const fetchLocationsForSignup = async () => {
  const url = buildApiUrl(API_CONFIG.endpoints.locations.get);
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json' }
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch locations: ${response.statusText}`);
  }
  const json = await response.json();
  const raw = json.data ?? json;
  if (!Array.isArray(raw)) return [];
  return raw.map((location) => {
    if (typeof location === 'string') return location;
    if (location?.name) return location.name;
    if (location?.city && location?.state) return `${location.city}, ${location.state}`;
    if (location?.address) return location.address;
    return String(location?.id || '');
  }).filter(Boolean);
};
//dfadsfsdadsfad
//dfsdfhdsfmvvvvvvhfnvhjfdnvmhfdnmhvfddsdfgfdgsf 
// Get coach dashboard data
export const getCoachDashboard = async (coachId) => {
  try {
    const url = buildApiUrl(`/coaches/${coachId}/dashboard`);
    return await coachApiCall(url);
  } catch (error) {
    console.error('Error fetching coach dashboard:', error);
    throw error;
  }
};

// Get coach's assigned teams
export const getCoachTeams = async (coachId) => {
  try {
    const url = buildApiUrl(API_CONFIG.endpoints.coaches.getAssignments, { id: coachId });
    return await coachApiCall(url);
  } catch (error) {
    console.error('Error fetching coach teams:', error);
    throw error;
  }
};

// ----------------- COACH ASSIGNMENTS (read-only) -----------------

export const getCoachAssignments = async (coachId) => {
  try {
    if (!coachId) throw new Error('coachId is required');
    const base = buildApiUrl(API_CONFIG.endpoints.coachAssignments.getAll);
    const url = `${base}?coachId=${encodeURIComponent(String(coachId))}`;
    console.log('Fetching coach assignments from:', url);
    const json = await coachApiCall(url);
    console.log('Coach assignments response:', json);
    return json.data ?? json;
  } catch (error) {
    console.error('Error fetching coach assignments:', error);
    console.error('Coach ID used:', coachId);
    
    // Temporary fallback: return empty array to prevent UI crash
    if (error.message.includes('500') || error.message.includes('Server error')) {
      console.warn('Returning empty array due to server error');
      return [];
    }
    
    // Re-throw with more context if needed
    throw error;
  }
};

// Send bulk message to team parents
export const sendBulkTeamMessage = async (messageData) => {
  try {
    const url = buildApiUrl(API_CONFIG.endpoints.parents.sendBulkEmail);
    return await coachApiCall(url, {
      method: 'POST',
      body: JSON.stringify(messageData)
    });
  } catch (error) {
    console.error('Error sending team message:', error);
    throw error;
  }
};

// Create practice event
export const createPractice = async (practiceData) => {
  try {
    const url = buildApiUrl(API_CONFIG.endpoints.events.create);
    return await coachApiCall(url, {
      method: 'POST',
      body: JSON.stringify({
        ...practiceData,
        type: 'practice'
      })
    });
  } catch (error) {
    console.error('Error creating practice:', error);
    throw error;
  }
};

// Report game score
export const reportScore = async (gameData) => {
  try {
    const url = buildApiUrl(API_CONFIG.endpoints.gameSchedules.update, { id: gameData.gameId });
    return await coachApiCall(url, {
      method: 'PUT',
      body: JSON.stringify({
        homeScore: gameData.homeScore,
        awayScore: gameData.awayScore,
        status: 'completed',
        reportedAt: new Date().toISOString()
      })
    });
  } catch (error) {
    console.error('Error reporting game score:', error);
    throw error;
  }
};

// ----------------- TIMESHEET -----------------

// Fetch all timesheet entries for a coach
export const getTimesheetEntries = async () => {
  try {
    const coachId = getCoachId();
    const url = buildApiUrl(API_CONFIG.endpoints.timesheets.get, { coachId });
    return await coachApiCall(url);
  } catch (error) {
    console.error('Error fetching timesheet entries:', error);
    throw error;
  }
};

// Create a new timesheet entry (submitted directly, no draft)
export const createTimesheetEntry = async (entryData) => {
  try {
    const coachId = getCoachId();
    const url = buildApiUrl(API_CONFIG.endpoints.timesheets.create, { coachId });
    return await coachApiCall(url, {
      method: 'POST',
      body: JSON.stringify({
        ...entryData,
        coachId: coachId,
        status: 'submitted',
        name: entryData.coachName || '',
        email: entryData.coachEmail || ''
      })
    });
  } catch (error) {
    console.error('Error creating timesheet entry:', error);
    throw error;
  }
};

// Update an existing timesheet entry
export const updateTimesheetEntry = async (entryId, updatedData) => {
  try {
    const coachId = getCoachId();
    const url = buildApiUrl(API_CONFIG.endpoints.timesheets.update, { coachId, id: entryId });
    return await coachApiCall(url, {
      method: 'PUT',
      body: JSON.stringify(updatedData)
    });
  } catch (error) {
    console.error('Error updating timesheet entry:', error);
    throw error;
  }
};

// Delete a timesheet entry
export const deleteTimesheetEntry = async (entryId) => {
  try {
    const coachId = getCoachId();
    const url = buildApiUrl(API_CONFIG.endpoints.timesheets.delete, { coachId, id: entryId });
    return await coachApiCall(url, {
      method: 'DELETE'
    });
  } catch (error) {
    console.error('Error deleting timesheet entry:', error);
    throw error;
  }
};

// Submit timesheet for review — NO LONGER USED (submit happens on create)
export const submitTimesheetEntry = async (entryId) => {
  try {
    const coachId = getCoachId();
    const url = buildApiUrl(API_CONFIG.endpoints.timesheets.submit, { coachId, id: entryId });
    return await coachApiCall(url, {
      method: 'POST',
      body: JSON.stringify({ status: 'submitted' })
    });
  } catch (error) {
    console.error('Error submitting timesheet entry:', error);
    throw error;
  }
};

// Get coach stats — fetches all entries for this coach and computes summary
export const getCoachStats = async () => {
  try {
    const coachId = getCoachId();
    const url = buildApiUrl(API_CONFIG.endpoints.timesheets.get, { coachId });
    const response = await coachApiCall(url);
    const entries = response.data ?? response ?? [];
    const arr = Array.isArray(entries) ? entries : [];
    const totalHours = arr.reduce((sum, e) => sum + (e.totalHours || 0), 0);
    const submitted = arr.filter((e) => e.status === 'submitted').length;
    const approved = arr.filter((e) => e.status === 'approved').length;
    const rejected = arr.filter((e) => e.status === 'rejected').length;
    return { data: { totalEntries: arr.length, totalHours, submittedEntries: submitted, approvedEntries: approved, rejectedEntries: rejected } };
  } catch (error) {
    console.error('Error fetching coach stats:', error);
    throw error;
  }
};

// ----------------- CURRICULUM -----------------

export const getCoachCurriculumItems = async () => {
  try {
    const url = buildApiUrl(API_CONFIG.endpoints.curriculum.getAll);
    const response = await coachApiCall(url);
    const rawItems = response?.data ?? response ?? [];
    if (!Array.isArray(rawItems)) return [];
    return rawItems
      .filter((item) => item?.active === true)
      .sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0));
  } catch (error) {
    console.error("Error fetching coach curriculum:", error);
    throw error;
  }
};

// ----------------- ADMIN TIMESHEET -----------------

// Admin: Get all timesheets with filters
export const getAllTimesheets = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add filter parameters
    if (filters.coachId) queryParams.append('coachId', filters.coachId);
    if (filters.location) queryParams.append('location', filters.location);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.limit) queryParams.append('limit', filters.limit);
    
    const url = buildApiUrl(`/admin/timesheets?${queryParams.toString()}`);
    return await coachApiCall(url);
  } catch (error) {
    console.error('Error fetching all timesheets:', error);
    throw error;
  }
};

// Admin: Get specific coach timesheets
export const getAdminCoachTimesheets = async (coachId, filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.status) queryParams.append('status', filters.status);
    
    const url = buildApiUrl(`/admin/timesheets/coach/${coachId}?${queryParams.toString()}`);
    return await coachApiCall(url);
  } catch (error) {
    console.error('Error fetching admin coach timesheets:', error);
    throw error;
  }
};

// Admin: Get timesheet statistics
export const getTimesheetStats = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    
    const url = buildApiUrl(`/admin/timesheets/stats?${queryParams.toString()}`);
    return await coachApiCall(url);
  } catch (error) {
    console.error('Error fetching timesheet stats:', error);
    throw error;
  }
};

// Admin: Approve timesheet
export const approveTimesheet = async (entryId, notes = '') => {
  try {
    const url = buildApiUrl(`/admin/timesheets/${entryId}/approve`);
    return await coachApiCall(url, {
      method: 'POST',
      body: JSON.stringify({ notes })
    });
  } catch (error) {
    console.error('Error approving timesheet:', error);
    throw error;
  }
};

// Admin: Reject timesheet
export const rejectTimesheet = async (entryId, reason = '') => {
  try {
    const url = buildApiUrl(`/admin/timesheets/${entryId}/reject`);
    return await coachApiCall(url, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  } catch (error) {
    console.error('Error rejecting timesheet:', error);
    throw error;
  }
};

// Admin: Export timesheets
export const exportTimesheets = async (filters = {}, format = 'json') => {
  try {
    const queryParams = new URLSearchParams();
    
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    queryParams.append('format', format);
    
    const url = buildApiUrl(`/admin/timesheets/export?${queryParams.toString()}`);
    
    if (format === 'csv') {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.text();
    }
    
    return await coachApiCall(url);
  } catch (error) {
    console.error('Error exporting timesheets:', error);
    throw error;
  }
};
/** Fetch all agreements ordered by the admin (used during onboarding). */
export const getAgreements = async () => {
  const url = buildApiUrl('/agreements');
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch agreements');
  const json = await response.json();
  return json.data ?? json;
};

export const updateOnboardStatus = async (coachId, status) => {
  try {
    const url = buildApiUrl(`/coaches/${coachId}`);
    return await coachApiCall(url, {
      method: 'PUT',
      body: JSON.stringify({ onboardStatus: status })
    });
  } catch (error) {
    console.error('Error updating onboard status:', error);
    throw error;
  }
};